import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { observer } from "mobx-react-lite";
import { Fragment, Suspense, useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router";

import { LoadingWebpart } from "@mr-portfolio-components/atoms/loading-webpart";
import { ConfirmDialogProvider } from "@mr-portfolio-components/molecules/confirm-dialog";
import { CurrencyToggle } from "@mr-portfolio-components/molecules/currency-toggle";
import { NotificationSnackbar } from "@mr-portfolio-components/molecules/notification-snackbar";
import { ThemeModeToggle } from "@mr-portfolio-components/molecules/theme-mode-toggle";
import { useStores } from "@mr-portfolio-store/use-stores";

import { routeLinks, routePaths } from "../../routes/route-paths";
import { useLabel } from "../../translations/use-label";
import { navigationGroups } from "./navigation-items";

const drawerWidth = 248;

export function LeftSidebarLayout(): React.JSX.Element {
  const getLabel = useLabel();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { liveUpdatesStore } = useStores();

  useEffect(() => {
    liveUpdatesStore.connect();

    return (): void => liveUpdatesStore.disconnect();
  }, [liveUpdatesStore]);

  return (
    <ConfirmDialogProvider>
      <Box sx={{ display: "flex" }}>
        <AppBar
          position="fixed"
          color="inherit"
          elevation={0}
          sx={{ zIndex: (appTheme) => appTheme.zIndex.drawer + 1, borderBottom: 1, borderColor: "divider" }}
        >
          <Toolbar sx={{ gap: { xs: 1, sm: 2 } }}>
            {!isDesktop && (
              <IconButton edge="start" aria-label={getLabel("navigation.openMenu")} onClick={() => setIsMobileMenuOpen(true)}>
                <MenuIcon />
              </IconButton>
            )}
            <Typography variant="h6" component="h1" noWrap sx={{ flexGrow: 1 }}>
              {getLabel("app.title")}
            </Typography>
            <CurrencyToggle />
            <ThemeModeToggle />
            <SignedInUserMenu />
          </Toolbar>
        </AppBar>
        <Drawer
          variant={isDesktop ? "permanent" : "temporary"}
          open={isDesktop || isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
          sx={{ width: drawerWidth, flexShrink: 0, "& .MuiDrawer-paper": { width: drawerWidth, boxSizing: "border-box" } }}
        >
          <Toolbar />
          <NavigationMenu onNavigate={() => setIsMobileMenuOpen(false)} />
        </Drawer>
        <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, md: 3 } }}>
          <Toolbar />
          <Suspense fallback={<LoadingWebpart />}>
            <Outlet />
          </Suspense>
        </Box>
      </Box>
      <NotificationSnackbar />
    </ConfirmDialogProvider>
  );
}

const SignedInUserMenu = observer(function SignedInUserMenu(): React.JSX.Element {
  const getLabel = useLabel();
  const { authStore } = useStores();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography variant="body2" noWrap sx={{ display: { xs: "none", sm: "block" } }}>
        {authStore.user?.userName}
      </Typography>
      <Tooltip title={getLabel("auth.signOut")}>
        <IconButton aria-label={getLabel("auth.signOut")} onClick={() => void authStore.signOut()}>
          <LogoutIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
});

const NavigationMenu = observer(function NavigationMenu({ onNavigate }: { onNavigate: () => void }): React.JSX.Element {
  const getLabel = useLabel();
  const { portfoliosStore } = useStores();
  const location = useLocation();

  useEffect(() => {
    void portfoliosStore.loadPortfolios();
    void portfoliosStore.loadPortfolioGroups();
  }, [portfoliosStore]);

  return (
    <>
      {navigationGroups.map((navigationGroup, groupIndex) => (
        <Fragment key={navigationGroup.groupKey}>
          {groupIndex > 0 && <Divider />}
          <List sx={{ "& .MuiListItemButton-root.active": { bgcolor: "action.selected", color: "primary.main" } }}>
            {navigationGroup.items.map((navigationItem) => (
              <Fragment key={navigationItem.path}>
                <ListItemButton
                  component={NavLink}
                  to={navigationItem.path}
                  end={navigationItem.path !== routePaths.instruments}
                  onClick={onNavigate}
                >
                  <ListItemIcon>{navigationItem.icon}</ListItemIcon>
                  <ListItemText primary={getLabel(navigationItem.labelKey)} />
                </ListItemButton>
                {navigationItem.showPortfolios === true &&
                  portfoliosStore.activePortfolios.map((portfolio) => {
                    const portfolioLink = routeLinks.portfolioDetail(portfolio.portfolioId);

                    return (
                      <ListItemButton
                        key={portfolio.portfolioId}
                        component={NavLink}
                        to={portfolioLink}
                        selected={location.pathname === portfolioLink}
                        onClick={onNavigate}
                        sx={{ pl: 9, py: 0.5 }}
                      >
                        <ListItemText primary={portfolio.name} slotProps={{ primary: { variant: "body2", noWrap: true } }} />
                      </ListItemButton>
                    );
                  })}
              </Fragment>
            ))}
          </List>
        </Fragment>
      ))}
    </>
  );
});
